// Policy Framework Service
// Complete CRUD operations for Policy Frameworks
// Part of Policy Framework & EU Diseases Implementation

import { db } from '../firebase';
import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  getDoc, 
  getDocs, 
  query, 
  where,
  orderBy,
  Timestamp,
  writeBatch
} from 'firebase/firestore';

class PolicyFrameworkService {
  constructor() {
    this.collectionName = 'policy_frameworks';
    this.collectionRef = collection(db, this.collectionName);
  }

  /**
   * Get all policy frameworks for an organization
   */
  async getPolicyFrameworks(organizationId, filters = {}) {
    try {
      let q = query(
        this.collectionRef,
        where('organizationId', '==', organizationId),
        where('isActive', '==', true)
      );

      // Apply filters
      if (filters.scope) {
        q = query(q, where('scope', '==', filters.scope));
      }

      if (filters.status) {
        q = query(q, where('status', '==', filters.status));
      }

      // Order by name
      q = query(q, orderBy('name', 'asc'));

      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting policy frameworks:', error);
      throw error;
    }
  }

  /**
   * Get single policy framework
   */
  async getPolicyFramework(frameworkId) {
    try {
      const docRef = doc(this.collectionRef, frameworkId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        throw new Error('Policy framework not found');
      }

      return {
        id: docSnap.id,
        ...docSnap.data()
      };
    } catch (error) {
      console.error('Error getting policy framework:', error);
      throw error;
    }
  }

  /**
   * Create new policy framework
   */
  async createPolicyFramework(data, userId, userName) {
    try {
      // Validation
      if (!data.organizationId) {
        throw new Error('Organization ID is required');
      }
      if (!data.name) {
        throw new Error('Framework name is required');
      }
      if (!data.scope) {
        throw new Error('Scope is required');
      }

      const now = Timestamp.now();

      const frameworkData = {
        ...data,
        createdAt: now,
        createdBy: userId,
        createdByName: userName || userId,
        updatedAt: now,
        updatedBy: userId,
        updatedByName: userName || userId,
        isActive: true,
        status: data.status || 'active',
        version: data.version || '1.0',
        // Ensure arrays exist
        regulatoryBasis: data.regulatoryBasis || {
          internationalStandards: [],
          nationalRegulations: [],
          hospitalPolicies: []
        },
        kpis: data.kpis || []
      };

      // Remove undefined values
      const cleanedData = this.removeUndefinedValues(frameworkData);

      const docRef = await addDoc(this.collectionRef, cleanedData);
      return docRef.id;
    } catch (error) {
      console.error('Error creating policy framework:', error);
      throw error;
    }
  }

  /**
   * Update policy framework
   */
  async updatePolicyFramework(frameworkId, updates, userId, userName) {
    try {
      const docRef = doc(this.collectionRef, frameworkId);
      
      const updateData = {
        ...updates,
        updatedAt: Timestamp.now(),
        updatedBy: userId,
        updatedByName: userName || userId
      };

      // Remove undefined values
      const cleanedData = this.removeUndefinedValues(updateData);

      await updateDoc(docRef, cleanedData);

      // Propagate name changes to related policy documents
      if (updates.name) {
        const policyDocsRef = collection(db, 'policy_documents');
        const q = query(
          policyDocsRef,
          where('policyFrameworkId', '==', frameworkId)
        );
        
        const snapshot = await getDocs(q);
        
        if (!snapshot.empty) {
          const batch = writeBatch(db);
          
          snapshot.docs.forEach(docSnap => {
            const docRef = doc(policyDocsRef, docSnap.id);
            batch.update(docRef, {
              policyFrameworkName: updates.name,
              updatedAt: Timestamp.now(),
              updatedBy: userId,
              updatedByName: userName || userId
            });
          });
          
          await batch.commit();
        }
      }

      return frameworkId;
    } catch (error) {
      console.error('Error updating policy framework:', error);
      throw error;
    }
  }

  /**
   * Delete (soft delete) policy framework
   * Now includes dependency checking - throws error if dependencies exist
   */
  async deletePolicyFramework(frameworkId, userId, userName, force = false) {
    // Check dependencies before deletion
    if (!force) {
      const { dependencyCheckService } = await import('./dependencyCheckService');
      const dependencyCheck = await dependencyCheckService.checkPolicyFrameworkDeletion(frameworkId);
      
      if (!dependencyCheck.canDelete && dependencyCheck.blockingDependencies.length > 0) {
        const errorMessages = dependencyCheck.blockingDependencies.map(d => d.message).join('; ');
        throw new Error(`Cannot delete policy framework: ${errorMessages}`);
      }
    }

    try {
      const docRef = doc(this.collectionRef, frameworkId);
      
      await updateDoc(docRef, {
        isActive: false,
        status: 'archived',
        updatedAt: Timestamp.now(),
        updatedBy: userId,
        updatedByName: userName || userId
      });

      return frameworkId;
    } catch (error) {
      console.error('Error deleting policy framework:', error);
      throw error;
    }
  }

  /**
   * Get frameworks by status
   */
  async getFrameworksByStatus(organizationId, status) {
    try {
      const q = query(
        this.collectionRef,
        where('organizationId', '==', organizationId),
        where('status', '==', status),
        where('isActive', '==', true),
        orderBy('name', 'asc')
      );

      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting frameworks by status:', error);
      throw error;
    }
  }

  /**
   * Get frameworks due for review
   */
  async getFrameworksDueForReview(organizationId) {
    try {
      const today = new Date();
      
      const allFrameworks = await this.getPolicyFrameworks(organizationId);
      
      return allFrameworks.filter(fw => {
        if (!fw.nextReviewDate) return false;
        const reviewDate = fw.nextReviewDate.toDate ? fw.nextReviewDate.toDate() : new Date(fw.nextReviewDate);
        return reviewDate <= today;
      });
    } catch (error) {
      console.error('Error getting frameworks due for review:', error);
      throw error;
    }
  }

  /**
   * Update KPI value
   */
  async updateKPI(frameworkId, kpiId, newValue, userId, userName) {
    try {
      const framework = await this.getPolicyFramework(frameworkId);
      
      const updatedKPIs = (framework.kpis || []).map(kpi => {
        if (kpi.kpiId === kpiId) {
          return {
            ...kpi,
            currentValue: newValue,
            lastUpdated: new Date(),
            status: this.determineKPIStatus(newValue, kpi.target)
          };
        }
        return kpi;
      });

      await this.updatePolicyFramework(
        frameworkId,
        { kpis: updatedKPIs },
        userId,
        userName
      );
    } catch (error) {
      console.error('Error updating KPI:', error);
      throw error;
    }
  }

  /**
   * Helper: Determine KPI status
   */
  determineKPIStatus(currentValue, target) {
    // Parse target (e.g., ">90%", "<5 per 1000")
    if (!target || !currentValue) return 'on-target';
    
    if (target.includes('>')) {
      const targetValue = parseFloat(target.replace(/[^0-9.]/g, ''));
      return currentValue >= targetValue ? 'on-target' : 'below-target';
    } else if (target.includes('<')) {
      const targetValue = parseFloat(target.replace(/[^0-9.]/g, ''));
      return currentValue <= targetValue ? 'on-target' : 'above-target';
    }
    return 'on-target';
  }

  /**
   * Helper: Remove undefined values from object
   */
  removeUndefinedValues(obj) {
    if (obj === null || obj === undefined) {
      return obj;
    }
    
    if (Array.isArray(obj)) {
      return obj.map(item => this.removeUndefinedValues(item));
    }
    
    if (typeof obj !== 'object') {
      return obj;
    }
    
    const cleaned = {};
    for (const [key, value] of Object.entries(obj)) {
      if (value !== undefined) {
        cleaned[key] = this.removeUndefinedValues(value);
      }
    }
    return cleaned;
  }
}

export default new PolicyFrameworkService();


